import React, { createContext, useContext, useState, ReactNode } from 'react'
import './Accordion.css'

interface IAccordionContext {
  expandedItems: string[]
  toggleItem: (id: string) => void
  allowMultiple: boolean
}

const AccordionContext = createContext<IAccordionContext | undefined>(undefined)

export const useAccordion = () => {
  const context = useContext(AccordionContext)
  if (!context) {
    throw new Error('useAccordion must be used within an Accordion component')
  }
  return context
}

interface IAccordionProps {
  children: ReactNode
  className?: string
  allowMultiple?: boolean
  defaultExpanded?: string[]
  expandedItems?: string[]
  onExpandedChange?: (expandedItems: string[]) => void
  isDarkMode?: boolean
}

const Accordion: React.FC<IAccordionProps> = ({
  children,
  className = '',
  allowMultiple = false,
  defaultExpanded = [],
  expandedItems: controlledExpandedItems,
  onExpandedChange,
  isDarkMode = true,
}) => {
  const [internalExpandedItems, setInternalExpandedItems] = useState<string[]>(defaultExpanded)

  // Use controlled state if provided, otherwise use internal state
  const expandedItems =
    controlledExpandedItems !== undefined ? controlledExpandedItems : internalExpandedItems

  const toggleItem = (id: string) => {
    const newExpandedItems = (() => {
      if (allowMultiple) {
        return expandedItems.includes(id)
          ? expandedItems.filter((item) => item !== id)
          : [...expandedItems, id]
      } else {
        return expandedItems.includes(id) ? [] : [id]
      }
    })()

    if (controlledExpandedItems !== undefined) {
      // Controlled mode - notify parent
      onExpandedChange?.(newExpandedItems)
    } else {
      // Uncontrolled mode - update internal state
      setInternalExpandedItems(newExpandedItems)
    }
  }

  const contextValue: IAccordionContext = {
    expandedItems,
    toggleItem,
    allowMultiple,
  }

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={`accordion ${isDarkMode ? 'dark' : 'light'} ${className}`}>{children}</div>
    </AccordionContext.Provider>
  )
}

export default Accordion
